import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import { Vocabulary } from "../../Utils/Vocabulary";
import { isMobile } from "../../Utils/Utils";
import { useCallback, useEffect, useState } from "react";
import { getData } from "../../Services/getData";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import ActivateNotifications from "../ActivateNotifications";
import useInterval from "../../Hooks/useInterval";
import VideoCallRoom from "../VideoCall/VideoCallRoom";
import LiveCountdown from "./LiveCountDown";
import moment from "moment";
import styles from "./LiveLegislation.module.css";
import { IOnlineLesson } from "../../Utils/Types";
import { useNavigate, useParams } from "react-router-dom";

const POLL_INTERVAL_MS = 60_000;
const LESSON_EXPIRY_HOURS = 1;

function getRoomName(lesson: IOnlineLesson): string {
  try {
    const url = new URL(lesson.lessonUrl);
    const path = url.pathname.replace(/^\//, "");
    if (path) return path;
  } catch {
    if (lesson.lessonUrl) return lesson.lessonUrl;
  }
  return `SLV-Online-${lesson._id}`;
}

function filterLessons(lessons: IOnlineLesson[]): IOnlineLesson[] {
  const cutoff = moment().subtract(LESSON_EXPIRY_HOURS, "hours");
  return lessons.filter((l) => moment(l.startDate).isAfter(cutoff));
}

function getInstructorName(lesson: IOnlineLesson): string | null {
  return lesson.instructor?.name || null;
}

function decodeRoomName(rawRoomName: string): string {
  try {
    return decodeURIComponent(rawRoomName);
  } catch {
    return rawRoomName;
  }
}

export default function LiveLegislation() {
  const { roomName } = useParams<{ roomName?: string }>();
  const navigate = useNavigate();
  const clientId = localStorage.getItem("clientId");

  const sanitize = (val: string | null) =>
    val && val !== "undefined" ? val : null;
  const displayName =
    sanitize(localStorage.getItem("fullName")) ||
    sanitize(localStorage.getItem("userName")) ||
    "Guest";

  const [lessons, setLessons] = useState<IOnlineLesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [notificationState, setNotificationState] = useState(false);
  // tracks lessons whose countdown has completed (join button becomes active)
  const [readyLessons, setReadyLessons] = useState<Set<string>>(new Set());

  const markReady = (id: string) =>
    setReadyLessons((prev) => new Set(prev).add(id));

  const fetchLessons = useCallback(
    (showLoader = true) => {
      if (!clientId) return;
      if (showLoader) setLoading(true);
      setError(false);
      getData(
        `${endpoints.onlineLessons.upcomingForClient}/${clientId}/upcoming`
      )
        .then((response) => {
          const raw: IOnlineLesson[] = Array.isArray(response.data)
            ? response.data
            : response.data
            ? [response.data]
            : [];
          setLessons(filterLessons(raw));
        })
        .catch(() => setError(true))
        .finally(() => {
          if (showLoader) setLoading(false);
        });
    },
    [clientId]
  );

  useEffect(() => {
    if (roomName) return;
    fetchLessons();
    getData(`${endpoints.client.main}/${clientId}`).then((response) => {
      if (response?.data) {
        setNotificationState(!!response.data.onlineLessonReminders);
      }
    });
  }, [clientId, roomName, fetchLessons]);

  useInterval(() => {
    if (roomName) return;
    if (!activeCallId) fetchLessons(false);
  }, POLL_INTERVAL_MS);

  if (roomName) {
    return (
      <Box sx={{ width: "100%", p: isMobile() ? 1 : 2 }}>
        <VideoCallRoom
          roomName={decodeRoomName(roomName)}
          displayName={displayName}
          onCallEnded={() => navigate(localUrlEnum.liveLegislation)}
        />
      </Box>
    );
  }

  const activeLesson = lessons.find((l) => l._id === activeCallId) ?? null;

  if (activeCallId && activeLesson) {
    return (
      <Box sx={{ width: "100%", p: isMobile() ? 1 : 2 }}>
        <VideoCallRoom
          roomName={getRoomName(activeLesson)}
          displayName={displayName}
          onCallEnded={() => setActiveCallId(null)}
        />
      </Box>
    );
  }

  const renderNotificationButton = () => (
    <div className={styles.notificationWrapper}>
      <ActivateNotifications
        notification={notificationState}
        setNotification={setNotificationState}
      />
    </div>
  );

  const renderLessonCard = (lesson: IOnlineLesson) => {
    const instructorName = getInstructorName(lesson);
    const hasStarted = moment(lesson.startDate).isSameOrBefore(moment());
    const isReady = hasStarted || readyLessons.has(lesson._id);

    return (
      <Box key={lesson._id} className={styles.lessonCard} sx={{ mb: 2 }}>
        <Typography variant="h6" className={styles.meetingTitle} gutterBottom>
          {lesson.title ?? Vocabulary.liveLegislationSession}
        </Typography>

        <Typography variant="body1" sx={{ mb: 0.5 }}>
          {moment(lesson.startDate).format("DD MMMM YYYY, HH:mm")}
        </Typography>

        {instructorName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {Vocabulary.instructor}: {instructorName}
          </Typography>
        )}

        {!isReady && (
          <LiveCountdown
            startTime={lesson.startDate}
            messageBefore={Vocabulary.liveInviteText}
            onComplete={() => markReady(lesson._id)}
          />
        )}

        {isReady && (
          <Button
            variant="contained"
            startIcon={<VideoCallIcon />}
            onClick={() =>
              navigate(
                `${localUrlEnum.liveLegislation}/${encodeURIComponent(
                  getRoomName(lesson)
                )}`
              )
            }
            sx={{ mt: 1 }}
          >
            {Vocabulary.joinSession}
          </Button>
        )}
      </Box>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="error" gutterBottom>
            {Vocabulary.liveSessionError}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchLessons()}
          >
            {Vocabulary.refresh}
          </Button>
        </Box>
      );
    }

    if (lessons.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" className={styles.noLiveText}>
            {Vocabulary.noLiveScheduled}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchLessons()}
            sx={{ mt: 2 }}
          >
            {Vocabulary.refresh}
          </Button>
        </Box>
      );
    }

    return (
      <Stack spacing={0}>
        {lessons.map(renderLessonCard)}
      </Stack>
    );
  };

  const hasLessons = !loading && !error && lessons.length > 0;

  return (
    <Grid
      container
      spacing={1}
      justifyContent="center"
      className={isMobile() ? styles.containerMobile : styles.container}
    >
      <Grid item xs={12} md={hasLessons ? 12 : 6} display="flex" alignItems="flex-start">
        <Box textAlign="center" width="100%">
          {renderNotificationButton()}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Typography variant="h4" className={styles.title}>
              {Vocabulary.accesLiveText}
            </Typography>
            {hasLessons && (
              <Tooltip title={Vocabulary.refresh}>
                <IconButton size="small" onClick={() => fetchLessons()}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {renderContent()}
        </Box>
      </Grid>

      {!hasLessons && (
        <Grid item xs={12} md={6}>
          <div className={styles.imageWrapper}>
            <Box
              component="img"
              src="/assets/images/training.png"
              alt="Live Training"
              className={styles.trainingImage}
            />
          </div>
        </Grid>
      )}
    </Grid>
  );
}
